import React, { useState, useEffect } from 'react';
import AddClientDialog from './AddClientDialog';
import AddItemDialog from './AddItemDialog';
import SenderProfileDialog from './SenderProfileDialog';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';

function InvoiceForm({ invoiceData, updateInvoiceData, setPdfUrl }) {
    const { t } = useLocale();
    const { currency } = useCurrency();
    const [showClientDialog, setShowClientDialog] = useState(false);
    const [showItemDialog, setShowItemDialog] = useState(false);
    const [showSenderDialog, setShowSenderDialog] = useState(false);
    const [items, setItems] = useState([]);
    const [client, setClient] = useState(null);
    const [senderProfile, setSenderProfile] = useState(null);
    const [paymentTerms, setPaymentTerms] = useState('14 Days');
    const [instructions, setInstructions] = useState('');
    const [taxRate, setTaxRate] = useState(10); // 10% default tax
    const [editItemIndex, setEditItemIndex] = useState(null);
    // pdfUrl is now managed in parent
    // const formRef = useRef();

    // Load from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem('invoiceFormData');
        if (saved) {
            const parsed = JSON.parse(saved);
            setClient(parsed.client || null);
            setSenderProfile(parsed.senderProfile || null);
            setItems(parsed.items || []);
            setPaymentTerms(parsed.paymentTerms || '14 Days');
            setInstructions(parsed.instructions || '');
            setTaxRate(parsed.taxRate || 10);
        }
    }, []);

    // Save to localStorage on change
    useEffect(() => {
        localStorage.setItem('invoiceFormData', JSON.stringify({
            client, senderProfile, items, paymentTerms, instructions, taxRate
        }));
    }, [client, senderProfile, items, paymentTerms, instructions, taxRate]);

    // Calculate totals whenever items or tax rate change
    useEffect(() => {
        const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const tax = (subtotal * taxRate) / 100;
        const total = subtotal + tax;

        updateInvoiceData({
            client,
            items,
            subtotal: subtotal.toFixed(2),
            tax: tax.toFixed(2),
            total: total.toFixed(2),
            paymentTerms,
            instructions,
            taxRate
        });
    }, [items, client, paymentTerms, instructions, taxRate, updateInvoiceData]);

    const handleAddClient = (clientData) => {
        setClient(clientData);
        setShowClientDialog(false);
    };

    const handleAddItem = (itemData) => {
        if (editItemIndex !== null) {
            setItems(items.map((item, idx) => idx === editItemIndex ? { ...itemData, id: item.id } : item));
            setEditItemIndex(null);
        } else {
            setItems([...items, { ...itemData, id: Date.now() }]);
        }
        setShowItemDialog(false);
    };

    const handleRemoveItem = (itemId) => {
        setItems(items.filter(item => item.id !== itemId));
    };

    const handleUpdateQuantity = (itemId, newQuantity) => {
        setItems(items.map(item => 
            item.id === itemId ? { ...item, quantity: parseInt(newQuantity) || 1 } : item
        ));
    };

    const handleEditItem = (itemId) => {
        const idx = items.findIndex(item => item.id === itemId);
        setEditItemIndex(idx);
        setShowItemDialog(true);
    };

    // Helper to build JSON body for API
    const buildApiBody = () => {
        // Map local state to API JSON structure
        // Helper for YYYY-MM-DDZ format
        const formatDateZ = (d) => d.toISOString().slice(0, 10) + 'Z';
        // Calculate due date based on paymentTerms
        const today = new Date();
        let daysToAdd = 0;
        if (paymentTerms === 'Today') daysToAdd = 0;
        else if (paymentTerms === '7 Days') daysToAdd = 7;
        else if (paymentTerms === '14 Days') daysToAdd = 14;
        else if (paymentTerms === '30 Days') daysToAdd = 30;
        const dueDateObj = new Date(today);
        dueDateObj.setDate(today.getDate() + daysToAdd);
        const dueDateStr = formatDateZ(dueDateObj);
        const todayStr = formatDateZ(today);

        return {
            invoiceSettings: {
                currencyCode: senderProfile?.currency || currency,
                prefix: 'INV',
                theme: 1,
                logoImage: senderProfile?.avatar || '',
            },
            invoice: {
                date: todayStr,
                dueDate: dueDateStr,
                createdAt: today.toISOString(),
                type: 'INVOICE',
                discountAmount: 0,
                discountType: 'PERCENTAGE',
                comments: instructions,
                invoiceItems: {
                    items: items.filter(i => !i.isExpense).map(i => ({
                        quantity: i.quantity,
                        item: {
                            name: i.name,
                            description: i.description,
                            rate: typeof i.price === 'string' ? parseFloat(i.price) || 0 : (i.price || 0),
                            tax: typeof i.taxPercent === 'string' ? parseFloat(i.taxPercent) || 0 : (i.taxPercent || 0),
                            taxType: 'PERCENTAGE',
                            isTaxInclusive: false,
                            unitType: 'hour',
                        }
                    }))
                },
                invoiceExpenses: {
                    items: items.filter(i => i.isExpense).map(i => ({
                        quantity: i.quantity,
                        expense: {
                            merchant: i.name,
                            category: '',
                            description: i.description,
                            total: typeof i.price === 'string' ? parseFloat(i.price) || 0 : (i.price || 0),
                            subtotal: typeof i.subtotal === 'string' ? parseFloat(i.subtotal) || 0 : (i.subtotal !== undefined ? i.subtotal : (typeof i.price === 'string' ? parseFloat(i.price) || 0 : (i.price || 0))),
                            tax: typeof i.tax === 'string' ? parseFloat(i.tax) || 0 : (i.tax !== undefined ? i.tax : 0),
                            tip: typeof i.tip === 'string' ? parseFloat(i.tip) || 0 : (i.tip !== undefined ? i.tip : 0),
                            images: null
                        }
                    }))
                },
                signatures: { items: [] }
            },
            client: {
                name: client?.billingName || '',
                email: client?.email || '',
                clientAddress: client?.billingAddress || '',
                phone: client?.phone || '',
                contactPerson: client?.contact || '',
                paymentTerms: paymentTerms || ''
            },
            user: {
                businessName: senderProfile?.businessName || '',
                contactName: senderProfile?.name || '',
                email: senderProfile?.email || '',
                phone: senderProfile?.phone || '',
                website: senderProfile?.website || '',
                userAddress: senderProfile?.address || '',
                chamberNumber: senderProfile?.chamberNo || '',
                companySummary: senderProfile?.companySummary || ''
            }
        };
    };

    // Handle PDF generation
    const handleGeneratePDF = async () => {
        const body = buildApiBody();
        const languageMap = {
            English: 'en',
            Dutch: 'nl',
            French: 'fr',
            German: 'de',
            Italian: 'it',
            Spanish: 'es'
        };
        const lang = languageMap[senderProfile?.language] || 'en';
        const endpoint = `https://core.invoicepoint.app/${lang}/generate/pdf/data`;
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to generate PDF');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            if (typeof setPdfUrl === 'function') setPdfUrl(url);
        } catch (e) {
            alert('Error generating PDF: ' + e.message);
        }
    };

    return (
        <div className="invoice-form">
            <div className="form-header">
                <h2 className="form-title">{t('invoiceForm.title')}</h2>
            </div>

            {/* Sender Profile Section */}
            <div className="form-section">
                <label className="section-label">{t('invoiceForm.senderProfileLabel')}</label>
                <button 
                    className="add-button"
                    onClick={() => setShowSenderDialog(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#27C492">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-2.21 3.58-4 8-4s8 1.79 8 4" />
                    </svg>
                    <span>{senderProfile ? senderProfile.businessName : t('invoiceForm.addSenderProfile')}</span>
                    <svg className="arrow-right" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Client Section */}
            <div className="form-section">
                <label className="section-label">{t('invoiceForm.billTo')}</label>
                <button 
                    className="add-button"
                    onClick={() => setShowClientDialog(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#27C492">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{client ? client.billingName : t('invoiceForm.addClient')}</span>
                    <svg className="arrow-right" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Invoice Items Section */}
            <div className="form-section">
                <label className="section-label">{t('invoiceForm.invoiceItemsLabel')}</label>
                <div className="items-list">
                    {items.map((item) => (
                        <div key={item.id} className="invoice-item" onClick={() => handleEditItem(item.id)} style={{cursor: 'pointer'}}>
                            <div className="item-details">
                                <div className="item-header">
                                    <h4 className="item-name">{item.name}</h4>
                                    <input 
                                        type="number" 
                                        className="item-quantity"
                                        value={item.quantity}
                                        onChange={(e) => handleUpdateQuantity(item.id, e.target.value)}
                                        min="1"
                                    />
                                    <button 
                                        className="remove-item"
                                        onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <p className="item-description">{item.description}</p>
                                <p className="item-price">€{item.price}</p>
                                {item.isExpense && <span className="expense-badge">{t('invoiceForm.expenseBadge')}</span>}
                            </div>
                        </div>
                    ))}
                </div>
                <button 
                    className="add-item-button"
                    onClick={() => setShowItemDialog(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#27C492">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>{t('invoiceForm.addItem')}</span>
                </button>
            </div>

            {/* Amount Section */}
            <div className="form-section d-none">
                <label className="section-label">{t('invoiceForm.amountLabel')}</label>
                <div className="amount-details">
                    <div className="amount-row">
                        <span>{t('invoiceForm.subtotal')}</span>
                        <span>€{invoiceData.subtotal || '0.00'}</span>
                    </div>
                    <div className="amount-row tax-row">
                        <div>
                            <span>{t('invoiceForm.taxes')}</span>
                            <input 
                                type="number" 
                                className="tax-input"
                                value={taxRate}
                                onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                                min="0"
                                max="100"
                            />
                            <span className="tax-percent">%</span>
                        </div>
                        <span>€{invoiceData.tax || '0.00'}</span>
                    </div>
                    <div className="amount-row total-row">
                        <span>{t('invoiceForm.total')}</span>
                        <span>€{invoiceData.total || '0.00'}</span>
                    </div>
                </div>
            </div>

            {/* Payment Terms */}
            <div className="form-section">
                <label className="section-label">{t('invoiceForm.paymentTermsLabel')}</label>
                <div className="payment-terms">
                    {[
                        { value: 'Today', label: t('invoiceForm.today') },
                        { value: '7 Days', label: t('invoiceForm.sevenDays') },
                        { value: '14 Days', label: t('invoiceForm.fourteenDays') },
                        { value: '30 Days', label: t('invoiceForm.thirtyDays') },
                    ].map((term) => (
                        <button
                            key={term.value}
                            className={`term-button ${paymentTerms === term.value ? 'active' : ''}`}
                            onClick={() => setPaymentTerms(term.value)}
                        >
                            {term.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Instructions */}
            <div className="form-section">
                <label className="section-label">{t('invoiceForm.instructionLabel')}</label>
                <textarea
                    className="instructions-input"
                    placeholder={t('invoiceForm.instructionPlaceholder')}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows="4"
                />
            </div>

            {/* Generate Button */}
            <button
                className="generate-button"
                onClick={handleGeneratePDF}
                disabled={
                    !senderProfile ||
                    !senderProfile.name ||
                    !senderProfile.businessName ||
                    !client ||
                    !client.billingName ||
                    items.length === 0
                }
            >
                {t('invoiceForm.generatePdf')}
            </button>

            {/* Dialogs */}
            {showClientDialog && (
                <AddClientDialog 
                    onClose={() => setShowClientDialog(false)}
                    onSave={handleAddClient}
                    initialData={client}
                />
            )}
            {showItemDialog && (
                <AddItemDialog 
                    onClose={() => {
                        setShowItemDialog(false);
                        setEditItemIndex(null);
                    }}
                    onSave={handleAddItem}
                    initialData={editItemIndex !== null ? items[editItemIndex] : null}
                />
            )}
            {showSenderDialog && (
                <SenderProfileDialog
                    onClose={() => setShowSenderDialog(false)}
                    onSave={profile => {
                        setSenderProfile(profile);
                        setShowSenderDialog(false);
                    }}
                    initialData={senderProfile}
                />
            )}
            {/* PDF preview is now handled in the preview pane */}
        </div>
    );
}

export default InvoiceForm;
